import React, { useMemo } from 'react';
import { Button } from './ui/button';
import { Plus, XCircle, Building2, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { cyclesAPI } from '../lib/api';
import RosterAddStudentPicker from './RosterAddStudentPicker';
import { usePagination, PaginationBar } from './PaginationBar';
import { LETTER_PILL } from './CycleBoard';
import { bySurname, byName } from '../lib/sort';

const IndustryLetterPill = ({ letter, hasStudents, onClick, rid }) => {
  if (!hasStudents && (!letter || letter.status === 'not_created')) {
    return <span className="text-[10px] text-gray-300 w-[92px] text-center hidden md:inline-block">—</span>;
  }
  const pill = LETTER_PILL[letter?.status || 'not_created'] || LETTER_PILL.not_created;
  return (
    <button
      type="button"
      data-testid={`participants-industry-letter-${rid}`}
      title={`Industry letter: ${pill.label}${letter?.signed_by ? ` by ${letter.signed_by}` : ''}`}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1 text-[10px] font-medium rounded px-1.5 py-0.5 w-[92px] shrink-0 hover:opacity-80 ${pill.cls}`}
    >
      <pill.Icon className="w-3 h-3" /> {pill.label}
    </button>
  );
};

const Pane = ({ icon: Icon, title, meta, action, children, testId }) => (
  <div className="border rounded-md bg-white flex flex-col min-h-[320px]" data-testid={testId}>
    <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between gap-2 rounded-t-md">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        <Icon className="w-3.5 h-3.5" /> {title} <span className="normal-case font-normal text-gray-400">{meta}</span>
      </div>
      {action}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

const FillBar = ({ filled, capacity }) => {
  const pct = capacity > 0 ? Math.min(100, Math.round((filled / capacity) * 100)) : 0;
  const full = capacity > 0 && filled >= capacity;
  const open = Math.max(capacity - filled, 0);
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden hidden sm:block">
        <div className={`h-full ${full ? 'bg-amber-500' : filled === 0 ? 'bg-gray-300' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs font-semibold w-[88px] text-right ${full ? 'text-amber-700' : filled === 0 ? 'text-gray-500' : 'text-emerald-700'}`}>
        {filled}/{capacity}{full ? ' · full' : ` · ${open} open`}
      </span>
    </div>
  );
};

/**
 * Participants tab — roster (left) and restaurants (right) side by side.
 * These are the two inputs to the Board, so they're edited together.
 */
export const CycleParticipants = ({ cycle, canWrite, onCycle, onAddRestaurant, onRestaurantClick, onIndustryLetterClick }) => {
  const live = useMemo(() => (cycle.placements || []).filter((p) => p.status === 'active' || p.status === 'pending'), [cycle.placements]);
  const restById = useMemo(() => Object.fromEntries((cycle.restaurants || []).map((r) => [r.id, r])), [cycle.restaurants]);
  const studentById = useMemo(() => Object.fromEntries((cycle.students || []).map((s) => [s.id, s])), [cycle.students]);
  const placementByStudent = useMemo(() => Object.fromEntries(live.map((p) => [p.student_id, p])), [live]);
  const placementsByRest = useMemo(() => {
    const m = {};
    live.forEach((p) => { (m[p.restaurant_id] = m[p.restaurant_id] || []).push(p); });
    return m;
  }, [live]);
  const totalSeats = (cycle.restaurants || []).reduce((n, r) => n + (r.available_positions || 0), 0);
  const unplaced = (cycle.students || []).filter((s) => !placementByStudent[s.id]).length;

  const run = async (fn, ok) => {
    try {
      const r = await fn();
      onCycle(r.data);
      toast.success(ok);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const shortName = (name = '') => {
    const [first, ...rest] = name.split(' ');
    return rest.length ? `${first} ${rest[rest.length - 1][0]}.` : first;
  };

  const students = useMemo(() => [...(cycle.students || [])].sort((a, b) => {
    const aP = placementByStudent[a.id] ? 1 : 0;
    const bP = placementByStudent[b.id] ? 1 : 0;
    return aP - bP || bySurname(a, b);
  }), [cycle.students, placementByStudent]);
  const restaurants = useMemo(() => [...(cycle.restaurants || [])].sort(byName), [cycle.restaurants]);
  const pgS = usePagination(students, 'cycle-roster');
  const pgR = usePagination(restaurants, 'cycle-restaurants');

  return (
    <div className="space-y-3" data-testid="cycle-participants">
      {canWrite && (
        <RosterAddStudentPicker
          cycle={cycle}
          onAdd={(sid) => run(() => cyclesAPI.addStudent(cycle.id, sid), 'Student added to cycle')}
        />
      )}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <Pane
            icon={GraduationCap}
            title={`Roster · ${students.length}`}
            meta={unplaced > 0 ? `· ${unplaced} unplaced` : '· all placed'}
            testId="participants-roster"
          >
            {students.length === 0 ? (
              <p className="text-sm text-gray-500 italic p-4" data-testid="empty-roster-message">
                No students in this cycle yet. Use Add student above, or adjust the cohort fields on the Settings tab.
              </p>
            ) : (
              <>
              <PaginationBar {...pgS.bar} position="top" noun="students" className="border-t-0" />
              <ul className="divide-y" data-testid="cycle-roster">
                {pgS.pageItems.map((s) => {
                  const p = placementByStudent[s.id];
                  const manual = (cycle.additional_student_ids || []).includes(s.id);
                  return (
                    <li key={s.id} data-testid={`roster-row-${s.id}`} className="px-3 py-2 flex items-center justify-between gap-2 group">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
                          {s.name}
                          {manual && <span className="text-[9px] uppercase tracking-wide bg-ica-gold/20 text-ica-navy font-semibold rounded px-1 py-0.5">manual</span>}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">{s.student_number}</div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {p ? (
                          <span className="text-[11px] text-emerald-700 truncate max-w-[140px]" title={restById[p.restaurant_id]?.name}>
                            placed · {restById[p.restaurant_id]?.name || '—'}
                          </span>
                        ) : (
                          <span className="text-[11px] text-amber-700 font-semibold">unplaced</span>
                        )}
                        {canWrite && (
                          <button
                            type="button"
                            data-testid={`remove-student-${s.id}`}
                            title="Remove from cycle"
                            onClick={() => run(() => cyclesAPI.removeStudent(cycle.id, s.id), 'Student removed from cycle')}
                            className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600 p-0.5 rounded transition-opacity"
                          >
                            <XCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
              <PaginationBar {...pgS.bar} position="bottom" noun="students" />
              </>
            )}
            {(cycle.excluded_student_ids || []).length > 0 && (
              <div className="px-3 py-2 border-t">
                <p className="text-[11px] text-gray-500 mb-1.5">Excluded ({cycle.excluded_student_ids.length}) — click to restore</p>
                <div className="flex flex-wrap gap-1.5">
                  {cycle.excluded_student_ids.map((sid) => (
                    <button
                      key={sid}
                      data-testid={`restore-student-${sid}`}
                      onClick={() => run(() => cyclesAPI.includeStudent(cycle.id, sid), 'Student restored')}
                      className="text-[11px] px-2 py-0.5 border rounded bg-white hover:bg-ica-navy/5 hover:border-ica-navy text-ica-navy"
                    >
                      {studentById[sid]?.name || `${sid.slice(0, 8)}…`} ↩
                    </button>
                  ))}
                </div>
              </div>
            )}
          </Pane>
        </div>

        <div className="lg:col-span-3">
          <Pane
            icon={Building2}
            title={`Restaurants · ${restaurants.length}`}
            meta={`· ${live.length}/${totalSeats} seats filled · ${cycle.industry_letters_signed || 0}/${cycle.industry_letters_total || 0} industry letters signed`}
            testId="participants-restaurants"
            action={canWrite && (
              <Button data-testid="add-restaurant-btn" size="sm" variant="outline" className="h-7 text-xs" onClick={onAddRestaurant}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add restaurant
              </Button>
            )}
          >
            {restaurants.length === 0 ? (
              <p className="text-sm text-gray-500 italic p-4">No restaurants added yet.</p>
            ) : (
              <>
              <PaginationBar {...pgR.bar} position="top" noun="restaurants" className="border-t-0" />
              <ul className="divide-y">
                {pgR.pageItems.map((r) => {
                  const rp = placementsByRest[r.id] || [];
                  return (
                    <li key={r.id} data-testid={`cycle-restaurant-${r.id}`} className="px-3 py-2 flex items-center gap-3 group">
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          data-testid={`participants-restaurant-link-${r.id}`}
                          onClick={() => onRestaurantClick?.(r)}
                          className="text-sm font-medium text-ica-navy hover:text-ica-gold hover:underline truncate block text-left max-w-full"
                        >
                          {r.name}
                        </button>
                        <div className={`text-[11px] truncate ${rp.length ? 'text-gray-500' : 'text-gray-400 italic'}`}>
                          {rp.length ? [...rp].map((p) => studentById[p.student_id]).filter(Boolean).sort(bySurname).map((s) => shortName(s.name)).join(', ') : 'no students yet'}
                        </div>
                      </div>
                      <FillBar filled={rp.length} capacity={r.available_positions || 0} />
                      <IndustryLetterPill
                        rid={r.id}
                        letter={r.industry_letter}
                        hasStudents={rp.length > 0}
                        onClick={() => (onIndustryLetterClick || onRestaurantClick)?.(r)}
                      />
                      {canWrite && (
                        <button
                          type="button"
                          data-testid={`remove-restaurant-${r.id}`}
                          title="Remove from cycle"
                          onClick={() => run(() => cyclesAPI.removeRestaurant(cycle.id, r.id), 'Restaurant removed')}
                          className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-gray-400 hover:text-red-600 p-0.5 rounded transition-opacity"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              <PaginationBar {...pgR.bar} position="bottom" noun="restaurants" />
              </>
            )}
          </Pane>
        </div>
      </div>
    </div>
  );
};

export default CycleParticipants;
